import { z } from "zod";
import { ok, err } from "./result.js";
/**
 * The diegetic boundary (CLAUDE.md Law 2). Two branded string types that are
 * NOT interchangeable at the type level:
 *
 *   Prose          — learner-facing NARRATIVE text (Narrator output, NPC
 *                    dialogue). Constructible only through parseProse(),
 *                    which rejects the FULL mechanical-vocabulary list.
 *   MechanicalNote — engine-internal justification text (modifier reasons,
 *                    band "gain" instructions, devil's-bargain terms). May
 *                    freely contain mechanical vocabulary — that is its job.
 *
 * A third schema, DocumentText, exists alongside these for authored CASE
 * DOCUMENT bodies and fidelity banners (packages/documents). It is a
 * DIFFERENT, narrower validator, not a relaxed Prose — see the note above
 * DOCUMENT_BANNED_TERM_ENTRIES for why the two lists must not be merged.
 *
 * A raw string is never assignable to either without going through a parser,
 * because .brand() adds a phantom property no plain string literal carries.
 */
const BANNED_MECHANICAL_TERM_ENTRIES = [
    { term: "dice", pattern: /\bdice\b/i },
    { term: "die", pattern: /\bdie\b/i },
    { term: "roll", pattern: /\brolls?(?:ed|ing)?\b/i },
    { term: "d20", pattern: /\bd20\b/i },
    { term: "DC", pattern: /\bDCs?\b/i },
    { term: "difficulty class", pattern: /\bdifficulty classe?s?\b/i },
    { term: "modifier", pattern: /\bmodifiers?\b/i },
    { term: "check", pattern: /\bchecks?(?:ed|ing)?\b/i },
    { term: "position", pattern: /\bpositions?(?:ed|ing)?\b/i },
    { term: "effect", pattern: /\beffects?\b/i },
    { term: "band", pattern: /\bbands?\b/i },
    { term: "clock", pattern: /\bclocks?(?:ed|ing)?\b/i },
    { term: "tick", pattern: /\bticks?(?:ed|ing)?\b/i },
    { term: "resource", pattern: /\bresources?\b/i },
    { term: "political capital", pattern: /\bpolitical capital\b/i },
    { term: "success", pattern: /\bsuccess(?:es|ful(?:ly)?|ion)?\b/i },
    { term: "succeed", pattern: /\bsucceeds?(?:ed|ing)?\b/i },
    { term: "failure", pattern: /\bfailures?\b/i },
    { term: "fail", pattern: /\bfails?(?:ed|ing)?\b/i },
    { term: "score", pattern: /\bscores?(?:d|ing)?\b/i },
    { term: "points", pattern: /\bpoints?\b/i },
    { term: "stat", pattern: /\bstats?\b/i },
    { term: "bonus", pattern: /\bbonuse?s?\b/i },
    { term: "penalty", pattern: /\bpenalt(?:y|ies)\b/i },
];
/**
 * Single source of truth for banned NARRATION vocabulary, shared by
 * parseProse, the contracts fixtures, and the Narrator eval harness.
 * Exported as a plain readonly array of term names — the regexes stay
 * module-private so nothing downstream depends on their exact shape.
 *
 * UNCHANGED by the parseProse/parseDocumentText split below (task section
 * 0's own framing: "parseProse(text) — for NARRATION only. Keeps the full
 * banned list.") — every existing caller of parseProse / this constant /
 * findBannedMechanicalTerms keeps its exact prior behavior.
 */
export const BANNED_MECHANICAL_TERMS = BANNED_MECHANICAL_TERM_ENTRIES.map((entry) => entry.term);
/**
 * CONTEXT-EXEMPT COLLOCATIONS (structure-and-rewards phase, ratified
 * decision 9.2). Ordinary-English phrases that word-boundary matching kept
 * killing in otherwise-good narration, observed live across hand-test
 * sessions: "He checks his watch", "basis points", "at some point",
 * "failing that", "veterinary roll-up", "Let me check". The phrase is
 * scrubbed BEFORE the banned-term scan, so the words inside it stop
 * tripping the matcher while every other use of the same words still does.
 * Every hit is reported via findBannedMechanicalTermsDetailed so exemption
 * use is loggable and abuse is visible in telemetry, per the ADR.
 */
/** A number said the way people say numbers: "7", "twenty-nine", "seventeen-and-a-half", "one and a half". */
const NUMBER_WORD = "(?:\\d[\\d.,]*|zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred)(?:-(?:one|two|three|four|five|six|seven|eight|nine))?(?:-and-a-(?:half|quarter))?";
const SPOKEN_FIGURE = `${NUMBER_WORD}(?:[- ]point[- ]${NUMBER_WORD})?`;
const EXEMPT_COLLOCATIONS = [
    { name: "checks-their-watch", pattern: /\bcheck(?:s|ed|ing)? (?:his|her|their|your|my) (?:watch|phone)\b/gi },
    { name: "basis-points", pattern: /\bbasis points?\b/gi },
    { name: "at-some-point", pattern: /\bat (?:some|that|this|one|no|any) point\b/gi },
    { name: "point-of-view", pattern: /\bpoint of view\b/gi },
    { name: "failing-that", pattern: /\bfailing that\b/gi },
    { name: "roll-up", pattern: /\broll-ups?\b/gi },
    { name: "let-me-check", pattern: /\blet me check\b/gi },
    // Narrow performance idiom observed in a synthetic scene. Mechanical uses of effect
    // remain prohibited; the exemption is reported by the existing telemetry scan.
    { name: "not-for-effect", pattern: /\b(?:not|isn['’]t|wasn['’]t|never|hardly)\s+for effect\b/gi },
    // Live 2026-09-09 (Wrenhall): "it points you to Corin Blay", "the index
    // points to", "your point", "point out" — ordinary English every one, and
    // each cost a written turn. "points" stays banned as a score.
    { name: "points-to", pattern: /\bpoint(?:s|ed|ing)? (?:(?:you|me|us|them|him|her) )?(?:to|toward|towards|at|out)\b/gi },
    { name: "the-point", pattern: /\b(?:the|your|my|his|her|their|our|a|no|whose) point(?:s)?\b/gi },
    // Compensation bands are case content (Harborlight's wage story); the R5 product run lost a
    // scene to "comp bands" (2026-09-15). A band of outcomes stays banned.
    { name: "pay-bands", pattern: /\b(?:comp|compensation|pay|salary|wage|grade|price|age|tax|internal|market) bands?\b/gi },
    // Checking as a verb with its object ("check with Renata", "checks the roster", "worth checking
    // whether", "it checks out") is ordinary English; "a check", "the check", "pass the check" stay
    // banned as the mechanic. The re-recorded playthrough lost a whole turn to "check" (2026-09-15).
    { name: "check-verb", pattern: /\b(?:double-|cross-|spot-|fact-)?check(?:s|ed|ing)?\s+(?:with|in|on|out|back|against|that|whether|if|what|how|who|where|the|those|these|this|his|her|their|my|your|its|it|them|him|every|each|both|all)\b|\bworth checking\b|\bchecks out\b|\bchecking\b|\b(?:I|we|you|she|he|they|to|go|let me|let['’]s|(?:I|we|you|she|he|they)['’]ll|(?:can|could|will|would|should|might|must)(?:\s+not|n['’]t)?)\s+check\b/gi },
    // Counting idioms: "basis-points" with a hyphen, and the other points that are not a score.
    { name: "counting-points", pattern: /\bbasis-points?\b|\b(?:percentage|data|talking|pain|decimal|sticking|selling|price|entry|bullet) points?\b/gi },
    // DOMAIN USE (persona pilots, 2026-09-17). 84 narrator sentences tripped the list and 29 scenes
    // fell back after the repair tripped it again; Larkmead, a case about a would-recommend rate,
    // lost a third of its scenes to "points". Figures, roll-outs, analysis effects, wall clocks,
    // checking as work, stances and business failures are ordinary English. The mechanic's own
    // phrasings stay banned: "you earn two points", "the check fails", "a desperate position",
    // "the effect is limited", "the clock ticks", "with the clock where it is".
    // A figure in points: "ten points", "one and a half points", "five point two points", "seventy to
    // ninety points", "a seventeen-and-a-half point spread". Never after a verb that awards or spends.
    { name: "figure-points", pattern: new RegExp(`(?<!\\b(?:earn|earns|earned|earning|gain|gains|gained|gaining|lose|loses|lost|losing|award|awards|awarded|spend|spends|spent|score|scores|scored|cost|costs|costing|get|gets|got|win|wins|won|deduct|deducts|deducted)\\s+(?:(?:you|yourself|them|him|her|us|another|an extra|extra)\\s+)?)\\b${SPOKEN_FIGURE}(?:\\s+and\\s+a\\s+(?:half|bit|quarter))?(?:\\s+(?:to|or|and)\\s+${SPOKEN_FIGURE})?[- ]points?\\b`, "gi") },
    // A spoken decimal: "four point six", "one-point-nine", and "a point and a half".
    { name: "spoken-decimal", pattern: new RegExp(`\\b${NUMBER_WORD}[- ]point[- ]${NUMBER_WORD}\\b|\\ba point and a half\\b`, "gi") },
    // Points of an argument: "either point", "on the second point", "at different points", "the whole point".
    { name: "points-of-argument", pattern: /\b(?:first|second|third|fourth|fifth|last|final|other|either|each|every|both|several|different|many|few|those|these|whole) points?\b/gi },
    { name: "roll-out-up", pattern: /\broll(?:s|ed|ing)?\s+(?:(?:it|them|this|that|these|those|everything)\s+)?(?:out|up|into)\b/gi },
    { name: "analysis-effect", pattern: /\bin effect\b|\b(?:mix|cohort|weight|weighting|price|pricing|volume|base|composition|selection|tenure|timing|survivorship|network|halo|knock-on|ripple|wealth|substitution|currency|scale|seasonal|calendar|denominator|sample|sampling|response|non-response|instrument|mode|channel|placebo|side|net|same|real|size|interaction|spillover|anchoring|framing|novelty)\s+effects?\b|\b(?:two|three|four|both|separate|different|these|those) effects\b/gi },
    { name: "wall-clock", pattern: /\bo['’]clock\b|\b(?:glance[sd]?|glancing|look(?:s|ed|ing)?|stare[sd]?|staring)\s+(?:up\s+)?at\s+the\s+(?:wall\s+)?clock\b|\bthe clock on the wall\b|\bthe (?:wall|office|kitchen|desk|station|lobby|carriage) clock\b/gi },
    { name: "checking-as-work", pattern: /\b(?:timing|consistency|sanity|reference|background|spot|quality|reality|credit|audit|compliance|safety|quick|final|second|health|gut|data|fact) checks?\b|\ba check of\b|\b(?:have|has|had|haven['’]t|hasn['’]t|hadn['’]t|I|we|she|he|they|you|been|was|were|already|never|just|not|double|and)\s+checked\b|\bchecked\s+(?:twice|again|before|first|already|it|that|them|this|those|these)\b|\b(?:you|we|I|they|she|he)\s+next\s+check\b/gi },
    { name: "stance-position", pattern: /\b(?:in|into)\s+a\s+(?:\w+\s+)?position\s+to\b|\bpositioned\s+to\b|\b(?:honest|official|negotiating|bargaining|legal|cash|net|market|competitive|public|stated|opening|fallback|formal|company['’]s|firm['’]s|board['’]s)\s+position\b|\bthat['’]s\s+a\s+position\b/gi },
    { name: "business-failure", pattern: /\b(?:retention|system|systems|process|control|controls|market|delivery|payment|service|equipment|audit|compliance|reporting|data|model|pipeline|supply|policy|communication|leadership|management|execution|business|recruitment|hiring|integration|pricing) failures?\b/gi },
];
/** Full scan: scrubs exempt collocations first, reports both what remained banned and which exemptions fired. */
export function findBannedMechanicalTermsDetailed(text) {
    let scrubbed = text;
    const exemptionsApplied = [];
    for (const collocation of EXEMPT_COLLOCATIONS) {
        if (collocation.pattern.test(scrubbed)) {
            exemptionsApplied.push(collocation.name);
            scrubbed = scrubbed.replace(collocation.pattern, " ");
        }
        collocation.pattern.lastIndex = 0;
    }
    const terms = [];
    for (const entry of BANNED_MECHANICAL_TERM_ENTRIES) {
        if (entry.pattern.test(scrubbed)) {
            terms.push(entry.term);
        }
    }
    return { terms, exemptionsApplied };
}
/** Every distinct banned NARRATION term found in `text`, in list order (exempt collocations scrubbed first). */
export function findBannedMechanicalTerms(text) {
    return findBannedMechanicalTermsDetailed(text).terms;
}
/**
 * The single canonical Prose schema. Used both directly (embedded as a field
 * type in other schemas throughout this package) and via parseProse below —
 * there is exactly one rule for what counts as valid Prose.
 */
export const ProseSchema = z
    .string()
    .superRefine((value, ctx) => {
    const terms = findBannedMechanicalTerms(value);
    if (terms.length > 0) {
        ctx.addIssue({
            code: "custom",
            message: `Prose must not contain mechanical vocabulary; found: ${terms.join(", ")}`,
        });
    }
})
    .brand();
/**
 * The only way to construct a Prose value. Never throws — returns a Result.
 * For NARRATION ONLY (Narrator output, NPC dialogue) — see parseDocumentText
 * below for case document bodies and fidelity banners, which is a
 * deliberately different, narrower validator.
 */
export function parseProse(input) {
    const terms = findBannedMechanicalTerms(input);
    if (terms.length > 0) {
        return err({ kind: "BANNED_MECHANICAL_VOCABULARY", terms, input });
    }
    const parsed = ProseSchema.safeParse(input);
    if (!parsed.success) {
        return err({ kind: "INVALID_PROSE", message: parsed.error.message });
    }
    return ok(parsed.data);
}
/**
 * DOCUMENT_BANNED_TERM_ENTRIES — CONTRACTS DEFECT FIX (task section 0).
 *
 * parseProse's narration list bans "position", "effect", "check", "score",
 * "points", "bonus", and "resource" — ordinary business English ("cost
 * position", "net effect", "resource planning", "check the reconciliation")
 * that will appear legitimately and constantly in authored case documents.
 * Applying the NARRATION validator to document bodies would corrupt real
 * case content, not protect it. Documents need their OWN, narrower banned
 * set: only the vocabulary that is UNAMBIGUOUSLY mechanical regardless of
 * context — a die-related term, an explicit rules abbreviation (DC,
 * "difficulty class"), a "modifier"/"roll", or an RPG-specific term
 * ("saving throw", "advantage", "disadvantage") has no ordinary-business
 * reading a real organisation's documents would ever produce; "position",
 * "effect", "check", "score", "points", "bonus", and "resource" very much
 * do, and are therefore NOT in this list.
 *
 * Band names (GREAT/SUCCESS/COST/MISS) are the one entry here that ISN'T a
 * simple case-insensitive word ban: "success", "cost", and "miss" are
 * completely ordinary lowercase/title-case business words ("a real
 * success", "cost of goods sold", "don't miss the deadline") that a
 * case-insensitive ban would corrupt exactly like "position"/"effect"
 * would. What the task asks to ban is "any literal reference to a band
 * name... AS MECHANICS" — a deliberately narrower, more specific thing than
 * the word itself. The deterministic proxy for "used as a mechanical
 * band-value token" adopted here is an exact, CASE-SENSITIVE, standalone
 * ALL-CAPS match (`\bSUCCESS\b`, not `/i`) — exactly the form a band value
 * takes when it leaks from code (`"band: MISS"`, `"resolved as SUCCESS"`),
 * and a form ordinary prose essentially never produces on its own (compare
 * "Q3 SUCCESS METRICS" as a slide title, which is a real, if rare,
 * false-positive risk this trade-off deliberately accepts in exchange for
 * catching a leaked literal band token, which is the actual failure mode
 * this validator exists to prevent).
 */
const DOCUMENT_BANNED_TERM_ENTRIES = [
    { term: "dice", pattern: /\bdice\b/i },
    { term: "die", pattern: /\bdie\b/i },
    { term: "d20", pattern: /\bd20\b/i },
    { term: "DC", pattern: /\bDCs?\b/i },
    { term: "difficulty class", pattern: /\bdifficulty classe?s?\b/i },
    { term: "modifier", pattern: /\bmodifiers?\b/i },
    { term: "roll", pattern: /\brolls?(?:ed|ing)?\b/i },
    { term: "saving throw", pattern: /\bsaving throws?\b/i },
    { term: "advantage", pattern: /\badvantage\b/i },
    { term: "disadvantage", pattern: /\bdisadvantage\b/i },
    { term: "GREAT (band)", pattern: /\bGREAT\b/ },
    { term: "SUCCESS (band)", pattern: /\bSUCCESS\b/ },
    { term: "COST (band)", pattern: /\bCOST\b/ },
    { term: "MISS (band)", pattern: /\bMISS\b/ },
];
/** Single source of truth for banned DOCUMENT vocabulary — see the note above DOCUMENT_BANNED_TERM_ENTRIES for why this list is deliberately narrower than BANNED_MECHANICAL_TERMS. */
export const DOCUMENT_BANNED_TERMS = DOCUMENT_BANNED_TERM_ENTRIES.map((entry) => entry.term);
/** Every distinct banned DOCUMENT term found in `text`, in list order. */
export function findBannedDocumentTerms(text) {
    const found = [];
    for (const entry of DOCUMENT_BANNED_TERM_ENTRIES) {
        if (entry.pattern.test(text)) {
            found.push(entry.term);
        }
    }
    return found;
}
/**
 * The canonical DocumentText schema — for case document bodies and fidelity
 * banners (packages/documents), NOT narration. Ordinary business vocabulary
 * ("position", "effect", "check", "score", "points", "bonus", "resource")
 * is permitted; see DOCUMENT_BANNED_TERM_ENTRIES for exactly what is not.
 */
export const DocumentTextSchema = z
    .string()
    .superRefine((value, ctx) => {
    const terms = findBannedDocumentTerms(value);
    if (terms.length > 0) {
        ctx.addIssue({
            code: "custom",
            message: `Document text must not contain mechanical vocabulary; found: ${terms.join(", ")}`,
        });
    }
})
    .brand();
/**
 * The only way to construct a DocumentText value. Never throws — returns a
 * Result. For case document bodies and fidelity banners/markers ONLY — see
 * parseProse above for narration.
 */
export function parseDocumentText(input) {
    const terms = findBannedDocumentTerms(input);
    if (terms.length > 0) {
        return err({ kind: "BANNED_MECHANICAL_VOCABULARY", terms, input });
    }
    const parsed = DocumentTextSchema.safeParse(input);
    if (!parsed.success) {
        return err({ kind: "INVALID_DOCUMENT_TEXT", message: parsed.error.message });
    }
    return ok(parsed.data);
}
/** The single canonical MechanicalNote schema — non-empty, otherwise unconstrained. */
export const MechanicalNoteSchema = z.string().min(1, "MechanicalNote must not be empty").brand();
/** The only way to construct a MechanicalNote value. Never throws. */
export function parseMechanicalNote(input) {
    const parsed = MechanicalNoteSchema.safeParse(input);
    if (!parsed.success) {
        return err({ kind: "INVALID_MECHANICAL_NOTE", message: parsed.error.message });
    }
    return ok(parsed.data);
}
//# sourceMappingURL=prose.js.map